import React, { useContext, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { ConfigContext } from "../../../../Api/global/ApiContext";
import { getAeAirlineBookings } from "../../../../Api/apiUtils";
import {
  Page,
  PageHeader,
  Card,
  Button,
  Field,
  Select,
  TextInput,
  Toolbar,
  Table,
  Loader,
  EmptyState,
} from "../../../../ui";
import "./aeReports.scss";

type AirlineCategory = { id: number; name: string };

type ClassDetail = {
  studentName?: string;
  teacherName?: string;
  studentParticipated?: string | number | boolean;
  teacherAvailable?: string | number | boolean;
};

type SlotDetail = {
  startTime?: string;
  endTime?: string;
  totalSlots?: number;
  slotsBooked?: number;
  teacherSlots?: number;
  teacherAvailable?: number;
  studentParticipated?: number;
  classDetails?: ClassDetail[];
};

const getToday = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const toNumber = (v: unknown): number => {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
};

const readSlotList = (raw: unknown): SlotDetail[] => {
  if (Array.isArray(raw)) return raw as SlotDetail[];
  if (!raw || typeof raw !== "object") return [];
  const o = raw as Record<string, unknown>;
  if (Array.isArray(o.content)) return o.content as SlotDetail[];
  const inner = o.content as Record<string, unknown> | undefined;
  if (inner && Array.isArray(inner.content)) return inner.content as SlotDetail[];
  if (o.data && typeof o.data === "object") {
    const d = o.data as Record<string, unknown>;
    if (Array.isArray(d.content)) return d.content as SlotDetail[];
    if (d.content && typeof d.content === "object") {
      const dInner = d.content as Record<string, unknown>;
      if (Array.isArray(dInner.content)) return dInner.content as SlotDetail[];
    }
  }
  return [];
};

const TeacherSlotSearch: React.FC = () => {
  const config = useContext(ConfigContext);
  const [courseId, setCourseId] = useState<string>("");
  const [slotDate, setSlotDate] = useState<string>(getToday());
  const [loading, setLoading] = useState(false);
  const [slotDetails, setSlotDetails] = useState<SlotDetail[]>([]);

  const categories: AirlineCategory[] = useMemo(
    () => (config?.AirlineCourseCategory || []) as AirlineCategory[],
    [config]
  );

  const fetchSlotData = async () => {
    if (!courseId) {
      toast.error("Please select course.");
      return;
    }
    setLoading(true);
    try {
      const result = await getAeAirlineBookings(slotDate, String(courseId));
      setSlotDetails(readSlotList(result));
    } catch (e) {
      console.error(e);
      toast.error("Could not load slot details.");
      setSlotDetails([]);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (slotDetails.length === 0) {
      toast.info("No slot data to export.");
      return;
    }
    const headers = [
      "Start Time",
      "End Time",
      "Total Slots",
      "Slots Booked",
      "Teacher Available",
      "Student Participated",
      "Student",
      "Teacher",
    ];
    const esc = (v: unknown) => {
      const s = `${v ?? ""}`.replace(/"/g, '""');
      return /[,"\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = slotDetails.map((s) => {
      const classDetails = Array.isArray(s.classDetails) ? s.classDetails : [];
      const students = classDetails.map((c) => c.studentName || "").join(" | ");
      const teachers = classDetails.map((c) => c.teacherName || "").join(" | ");
      return [
        s.startTime ?? "",
        s.endTime ?? "",
        toNumber(s.totalSlots),
        toNumber(s.slotsBooked),
        toNumber(s.teacherSlots ?? s.teacherAvailable),
        toNumber(s.studentParticipated),
        classDetails
          .map((detail) => `${detail.studentName || ""} - ${detail.teacherName || ""}`)
          .join(", "),
        "",
      ]
        .map(esc)
        .join(",");
    });
    const blob = new Blob([[headers.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "TeacherSlotDetails.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const courseOptions = [
    { value: "", label: "Select course" },
    ...categories.map((c) => ({ value: String(c.id), label: c.name })),
  ];

  return (
    <Page>
      <PageHeader eyebrow="AE Reports" title="Airline Slot Search" />

      <Card>
        <Toolbar>
          <Field label="Courses">
            {(ctl) => (
              <Select
                {...ctl}
                options={courseOptions}
                value={courseId}
                onChange={(e) => setCourseId(e.target.value)}
              />
            )}
          </Field>
          <Field label="Select date">
            {(ctl) => (
              <TextInput
                {...ctl}
                type="date"
                value={slotDate}
                onChange={(e) => setSlotDate(e.target.value)}
              />
            )}
          </Field>
          <div className="ae-reports__actions">
            <Button variant="primary" loading={loading} onClick={fetchSlotData}>
              Search
            </Button>
            <Button variant="secondary" onClick={exportCsv}>
              Export to CSV
            </Button>
          </div>
        </Toolbar>
      </Card>

      {loading ? (
        <Loader label="Loading slot details…" />
      ) : slotDetails.length === 0 ? (
        <EmptyState title="No Slots Available" description="Pick a course and date, then select Search." />
      ) : (
        <Card flush className="ae-reports__scroll ae-reports__scroll--62">
          <Table caption="Airline slot details">
            <thead>
              <tr>
                <th>Start Time</th>
                <th>End Time</th>
                <th className="ds-num">Total Slots</th>
                <th className="ds-num">Slots Booked</th>
                <th className="ds-num">Teacher Available</th>
                <th className="ds-num">Student Participated</th>
                <th>Student</th>
                <th>Teacher</th>
              </tr>
            </thead>
            <tbody>
              {slotDetails.map((s, i) => (
                <tr key={`${s.startTime || "s"}-${s.endTime || "e"}-${i}`}>
                  <td>{s.startTime || "—"}</td>
                  <td>{s.endTime || "—"}</td>
                  <td className="ds-num">{toNumber(s.totalSlots)}</td>
                  <td className="ds-num">{toNumber(s.slotsBooked)}</td>
                  <td className="ds-num">{toNumber(s.teacherSlots ?? s.teacherAvailable)}</td>
                  <td className="ds-num">{toNumber(s.studentParticipated)}</td>
                  <td colSpan={2}>
                    {Array.isArray(s.classDetails) && s.classDetails.length ? (
                      <table className="ae-pairs">
                        <tbody>
                          {(Array.isArray(s.classDetails) && s.classDetails.length
                            ? s.classDetails
                            : [{ studentName: "—", teacherName: "—" }]
                          ).map((c, idx) => (
                            <tr key={`${i}-${idx}`}>
                              <td>{c.studentName || "—"}</td>
                              <td>{c.teacherName || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      ""
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </Page>
  );
};

export default TeacherSlotSearch;
